import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { isNil, omitBy } from 'lodash-es';
import { z } from 'zod';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import type { RespondedType, ResponseError } from '@/models/response';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { nameSchema } from '@/utils/schemas';
import { generateSessionKey } from '@/utils/session';

export default function useUpdateGroupDetails(groupId: Nullable<number>) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, Partial<UpdateGroupRequest>>({
    mutationKey: ['/UpdateGroup', groupId],
    mutationFn: (params) => fetcher('/UpdateGroup', { ...omitBy(params, isNil), GroupID: groupId }),
    onSuccess: (_, params) => {
      queryClient.setQueryData(generateGroupInfoQueryOptions(groupId).queryKey, (data) =>
        produce(data, (draft) => {
          if (!draft?.GroupData) return;

          if (params.JoinType) {
            draft.GroupData.JoinType = params.JoinType;
          }
          if (params.AddFriend) {
            draft.GroupData.AddFriend = Boolean(params.AddFriend);
          }
          if (params.GroupName) {
            draft.GroupData.GroupName = params.GroupName;
            useConversationSessionStore.getState().updateSession(generateSessionKey('group', groupId), {
              GroupName: params.GroupName,
            });
          }
          if (params.GroupImg) {
            draft.GroupData.GroupImg = params.GroupImg;
            useConversationSessionStore.getState().updateSession(generateSessionKey('group', groupId), {
              GroupImg: params.GroupImg,
            });
          }
        }),
      );
      // Ensure lists refresh after a successful rename
      Promise.allSettled([
        queryClient.refetchQueries(groupsQueryOptions),
        queryClient.refetchQueries(allChatSessionQueryOptions),
      ]).catch(console.error);
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}

const updateGroupRequestScheme = z
  .object({
    GroupName: nameSchema,
    GroupImg: z.string(),
    AddFriend: z.union([z.literal(0), z.literal(1)]),
    JoinType: z.union([z.literal(0), z.literal(1)]),
  })
  .partial();

type UpdateGroupRequest = z.infer<typeof updateGroupRequestScheme>;
